import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        custom: "",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, style, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  
  // Enforce text/bg colors via inline styles to prevent CSS specificity wars.
  // Inline styles beat all CSS rules (even !important in stylesheets).
  let enforceStyle = style ? { ...style } : {};
  
  if (className) {
    const cls = ` ${className} `;
    
    // White background buttons MUST have black text
    if (cls.includes(' bg-white ') || cls.includes(' bg-[#FFFFFF] ')) {
      enforceStyle.color = enforceStyle.color || 'black';
    }
    
    // Explicit text-black
    if (cls.includes(' text-black ')) {
      enforceStyle.color = 'black';
    }
    
    // Explicit text-white (only if not a white-bg button)
    if (cls.includes(' text-white ') && !cls.includes(' bg-white ')) {
      enforceStyle.color = enforceStyle.color || 'white';
    }
  }
  
  // btn-primary always = white bg + black text
  if (className && className.includes('btn-primary')) {
    enforceStyle.backgroundColor = 'white';
    enforceStyle.color = 'black';
  }
  
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      style={Object.keys(enforceStyle).length > 0 ? enforceStyle : undefined}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
